import { contains, filter, map, property, values, size, isEmpty, negate, isFunction } from 'underscore';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultButton, PrimaryButton, Panel, PanelType, Stack } from '@bingads-webui-react/fluent-controls';
import { SuggestedItemsMultiSelect } from '@bingads-webui-campaign-react/pill-item-components';

const SUGGESTED_ITEMS_PANEL_DESRCIPTION = _TL_('These are sourced from existing ads that share the same Final URL as the current asset group or from your website. Microsoft Adverting’s advanced AI models may generate asset recommendations based on your website content.');

const deriveSuggestedItems = (availableRecommendations, fieldValues) => () => {
  const selectedRecommendations = filter(fieldValues, text => contains(availableRecommendations, text));

  return map(availableRecommendations, text => ({
    key: text,
    itemText: text,
    isSelected: contains(selectedRecommendations, text),
  }));
};

export const TextAssetRecommendationsPanel = ({
  i18n, onSave, onCancel, collection, availableRecommendations, maxCollectionSize, title, disabledSuggestionTooltip, reportDisplayedRecommendations,
}) => {
  const fieldValues = useMemo(() => values(collection), [collection]);
  const [suggestedItems, setSuggestedItems] = useState(deriveSuggestedItems(availableRecommendations, fieldValues));
  const selectedItemsCount = useMemo(() => size(suggestedItems.filter(property('isSelected'))), [suggestedItems]);
  const emptyFieldsCount = useMemo(() => maxCollectionSize - size(filter(fieldValues, negate(isEmpty))), [maxCollectionSize, fieldValues]);
  const remaningItemsCount = emptyFieldsCount - selectedItemsCount;

  useEffect(() => {
    if (!isEmpty(availableRecommendations) && isFunction(reportDisplayedRecommendations)) {
      reportDisplayedRecommendations(availableRecommendations);
    }
  }, [availableRecommendations, reportDisplayedRecommendations]);

  const onSuggestedItemToggle = useCallback((itemIndex, itemText) => {
    setSuggestedItems(suggestedItems.map(item => ({
      ...item,
      isSelected: item.itemText === itemText ? !item.isSelected : item.isSelected,
    })));
  }, [suggestedItems]);

  const onSaveClick = useCallback(() => {
    onSave(suggestedItems.filter(property('isSelected')).map(item => item.itemText));
  }, [suggestedItems, onSave]);
  const onCancelClick = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const onRenderFooterContent = useCallback(() => (
    <Stack className="text-asset-recommendations-panel-buttons" horizontal tokens={{ childrenGap: 8 }}>
      <Stack.Item key="btn-add">
        <PrimaryButton className="btn-add" onClick={onSaveClick} disabled={!(selectedItemsCount > 0)}>
          {i18n.getString(_TL_('Add'))}
        </PrimaryButton>
      </Stack.Item>
      <Stack.Item key="btn-cancel">
        <DefaultButton className="btn-cancel" onClick={onCancelClick}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </Stack.Item>
    </Stack>
  ), [i18n, onSaveClick, onCancelClick, selectedItemsCount]);

  return (
    <Panel
      className={classNames('text-asset-recommendations-panel')}
      headerText={title}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      isOpen
      onDismiss={onCancelClick}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      type={PanelType.medium}
    >
      <SuggestedItemsMultiSelect
        suggestedItems={suggestedItems}
        onSuggestedItemToggle={onSuggestedItemToggle}
        suggestedSectionDescription={i18n.getString(SUGGESTED_ITEMS_PANEL_DESRCIPTION)}
        disableAllUnselectedSuggestions={!(remaningItemsCount > 0)}
        disabledSuggestionTooltip={disabledSuggestionTooltip}
      />
    </Panel>
  );
};

TextAssetRecommendationsPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  collection: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))).isRequired,
  availableRecommendations: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxCollectionSize: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  disabledSuggestionTooltip: PropTypes.string.isRequired,
  reportDisplayedRecommendations: PropTypes.func,
};

TextAssetRecommendationsPanel.defaultProps = {
  reportDisplayedRecommendations: undefined,
};
